import { lazy } from "react";
import { createBrowserRouter, type RouteObject } from "react-router-dom";
import { AppLayout } from "./AppLayout";
import { RequireAuth } from "./RequireAuth";
import { RequireTenantSelection } from "../auth/tenantPicker";
import { RequireRole } from "./RequireRole";
import { TenantRespondEntry } from "./TenantRespondEntry";
import { PageTitleRoot, type RouteTitleHandle } from "./pageTitle";
// THE PRE-SESSION PAGES, and the only page components this module imports statically. What a person who
// has not signed in downloads is exactly this table plus these four — see ./signedInPages for the
// measurement that made the boundary necessary and for why it is ONE module and not one per page.
import { LoginPage } from "../pages/LoginPage";
import { AuthCallbackPage } from "../pages/AuthCallbackPage";
import { EmailLinkRequestPage } from "../pages/EmailLinkRequestPage";
import { EmailLinkRedeemPage } from "../pages/EmailLinkRedeemPage";

// The signed-in surface, behind one `import()`. Every one of these resolves the SAME module, so the
// first page inside the shell fetches it and every navigation after that is already in hand; AppShell
// holds the Suspense boundary they land in. The route table below is unchanged — `lazy` gives back a
// component, so each `element: <Name />` reads exactly as it did when these were static imports, which
// is what `resolveExitTarget`, page-title.test.mjs and change-management-frame.test.mjs rely on.
const MyLearningPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.MyLearningPage })));
const ScormRunPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.ScormRunPage })));
const ContentPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.ContentPage })));
const CourseEditPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.CourseEditPage })));
const ScormPreviewPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.ScormPreviewPage })),
);
const AssignTrainingPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.AssignTrainingPage })),
);
const GroupsPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.GroupsPage })));
const UsersPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.UsersPage })));
const ViewScopeGrantsPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.ViewScopeGrantsPage })),
);
const OrganizationPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.OrganizationPage })),
);
const AuditLogPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.AuditLogPage })));
const CoverageReportPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.CoverageReportPage })),
);
const QuestionnaireListPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireListPage })),
);
const QuestionnaireEditPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireEditPage })),
);
const QuestionnaireReportPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireReportPage })),
);
const LeadershipReportPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.LeadershipReportPage })),
);
const LeadershipReportListPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.LeadershipReportListPage })),
);
const QuestionnaireResultsListPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireResultsListPage })),
);
const QuestionnaireParticipationPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireParticipationPage })),
);
const QuestionnaireQuotesPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireQuotesPage })),
);
const QuestionnaireFormPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.QuestionnaireFormPage })),
);
const SupportAccessPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.SupportAccessPage })),
);
const TenantSettingsPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.TenantSettingsPage })),
);
const ChangeProgramListPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.ChangeProgramListPage })),
);
const ChangeProgramPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.ChangeProgramPage })),
);
const ChangeMapPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.ChangeMapPage })));
const ChangeReportPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.ChangeReportPage })),
);
const MyChangeActionsPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.MyChangeActionsPage })),
);
const EmploymentRecordsPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.EmploymentRecordsPage })),
);
const EmploymentRecordPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.EmploymentRecordPage })),
);
const CertificatePage = lazy(() => import("./signedInPages").then((m) => ({ default: m.CertificatePage })));
const CertificateOpsPage = lazy(() =>
  import("./signedInPages").then((m) => ({ default: m.CertificateOpsPage })),
);
const NotFoundPage = lazy(() => import("./signedInPages").then((m) => ({ default: m.NotFoundPage })));

/** A route's standing title, as an i18n key. Typed so a misspelt property name is a build error
 *  rather than a silently title-less bar. */
const titled = (titleKey: string): RouteTitleHandle => ({ titleKey });

// Route table. Public routes render standalone; everything under the app shell
// is gated by RequireAuth. Tenant-scoped `/t/<slug>` paths reuse the same tree
// (tenant is resolved from the URL in src/config).
//
// PAGE TITLES. A route under the shell names itself with `handle: { titleKey }` and AppLayout
// renders it as the top bar's <h1> (see ./pageTitle for the mechanism and the loading-state rule).
// EVERY route under the shell carries one (task b89df446): no page draws its own <h1> any more, so a
// route without a handle is a page with no heading at all — page-title.test.mjs fails the build on one.
// A title that is DATA (a course's name, a questionnaire's) is still declared here as the standing
// generic title; the page replaces it with usePageTitle() once its fetch lands.
//
// The PUBLIC routes above render outside the shell — there is no top bar to hand a title to — so they
// keep drawing their own <h1> (AuthLayout's `auth__title`). They carry a handle all the same: since
// task e46750d5 the same channel writes the BROWSER TAB, which every route has, shell or not. Their
// handle therefore names the tab only — it reaches no <h1>, because out here the page draws its own.
//
// The table is exported as DATA as well as a router: `resolveExitTarget` (./exitTarget) matches a
// candidate URL against it to decide whether a place still exists before navigating there, which only
// works while this array is the single declaration of what the SPA answers.
export const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage />, handle: titled("auth.loginTitle") },
  // OIDC authorization-code + PKCE redirect target (US-1.1). Public so the
  // `?code=` lands here and is exchanged before RequireAuth can bounce it.
  { path: "/auth/callback", element: <AuthCallbackPage />, handle: titled("auth.signingIn") },
  // External-learner email-link flow (US-1.3): request a link, then redeem the
  // one-time token from the email. Both are public (pre-session).
  { path: "/login/email", element: <EmailLinkRequestPage />, handle: titled("emailLink.heading") },
  {
    path: "/login/email/redeem",
    element: <EmailLinkRedeemPage />,
    handle: titled("emailLink.redeeming"),
  },
  // Tenant-addressable forms of the pre-session pages (`/t/<slug>/…`). These render the same components;
  // the tenant is read from the URL (src/config `resolveTenantSlug`) and forwarded to the backend as the
  // `X-Tenant` hint. Essential without a per-tenant subdomain (e.g. localhost / the E2E harness): the
  // email-link request+redeem carry no session, so the URL is the only place the tenant can come from.
  { path: "/t/:slug/login", element: <LoginPage />, handle: titled("auth.loginTitle") },
  {
    path: "/t/:slug/login/email",
    element: <EmailLinkRequestPage />,
    handle: titled("emailLink.heading"),
  },
  {
    path: "/t/:slug/login/email/redeem",
    element: <EmailLinkRedeemPage />,
    handle: titled("emailLink.redeeming"),
  },
  // …and the fourth, which is NOT a sign-in page: the landing of the mailed questionnaire link (task
  // f71bf1eb). It draws no page of the app — it forwards to the apex form on a session, and without one it
  // hands the arrival straight to THIS tenant's identity provider (the owner's decision of 3.11.2026, task
  // 09e495b1), which is the only way a respondent whose session has expired reaches their own
  // organisation's sign-in rather than a bare `/login` that can resolve none. The whole reason, and why
  // the shell is still mounted at `/` alone, is in ./TenantRespondEntry; the mail's side is
  // `QuestionnaireLinks.RespondUrl`.
  {
    path: "/t/:slug/questionnaires/:id/respond",
    element: <TenantRespondEntry />,
    handle: titled("questionnaireForm.title"),
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        {/* ADR-0018 §6.1 — a person whose token carries tenant MEMBERSHIPS chooses which organisation they
            are working in before the shell is drawn, and never before login (see auth/tenantPicker.tsx for
            why the choice cannot be made on the sign-in page). It is inside RequireAuth because the read it
            is drawn from needs the token, and above AppLayout because what it withholds is the whole
            tenant-scoped surface rather than one page of it. A person of one tenant passes straight
            through: their memberships list is empty and nothing is drawn. */}
        <RequireTenantSelection>
          <AppLayout />
        </RequireTenantSelection>
      </RequireAuth>
    ),
    children: [
      // The learner's own screen, one page with three tabs: Koulutukset at "/" and Todistukset at
      // "/certificates" (the 12.9.2026 merge), joined by Kyselyt at "/questionnaires" on 23.9.2026. The
      // SAME component is mounted on all three paths on purpose — the tabs are routes, so each is
      // bookmarkable and each keeps its own title, and `/certificates` survives the merge as the
      // addressable URL CertificatePage's back link points at. See MyLearningPage for the whole rationale.
      { index: true, element: <MyLearningPage />, handle: titled("home.heading") },
      // SCORM run view (US-3.1/3.2): launch/resume an attempt for a piece of
      // enrolled content and host the SCO. `?return=` sets the exit target.
      { path: "learn/:enrollmentId/:contentObjectId", element: <ScormRunPage />, handle: titled("scorm.title") },
      // Learner certificates (E6, US-6.1). "Todistukset" is the SECOND TAB of the learner's own page
      // above — the same component, selecting itself from the path — and lists the caller's issued
      // certificates (tunniste, myönnetty, voimassa asti, lataa PDF). "Omat todistukset" is this route's
      // page title (`certificates.title`, the `handle` below) and its nav entry (`nav.certificates`); the
      // tab itself renders `myLearning.tab.certificates`. The detail view renders/downloads a single
      // certificate keyed by its passed exam attempt — the target of the E4 exam-result link on a pass.
      // Learner-facing (every authenticated session), so not wrapped in RequireRole; the server scopes
      // results to the caller + tenant.
      { path: "certificates", element: <MyLearningPage />, handle: titled("certificates.title") },
      { path: "certificates/:attemptId", element: <CertificatePage />, handle: titled("certificate.title") },
      // Kyselymoottori RESPONDENT list (ehdotus 23.9.2026): "Kyselyt" is the THIRD TAB of the learner's
      // own page above — the same component again, selecting itself from the path — and lists the
      // caller's currently-open questionnaires with the way in to each. "Omat kyselyt" is this route's
      // page title (`myQuestionnaires.title`, the `handle` below) and its nav entry
      // (`nav.myQuestionnaires`); the tab itself renders `myLearning.tab.questionnaires`. Learner-facing
      // like the other two tabs, so no RequireRole: the server scopes the rows to the caller's own
      // participant rows, and the respond route below still decides who may actually answer. It opens
      // nothing the invitation email did not already open; it removes the need to still have the email.
      //
      // The bare path and `questionnaires/:id/respond` below are separate entries, so the static list
      // route can never swallow a respond URL and the respond route can never match this one.
      { path: "questionnaires", element: <MyLearningPage />, handle: titled("myQuestionnaires.title") },
      // Kyselymoottori RESPONDENT form (KM-E2 / US-E2.1): the questionnaire as the person answering it
      // sees it — the invitation link's target. Authenticated but NOT role-gated: eligibility is
      // membership of the questionnaire's frozen participant snapshot, decided server-side, and the page
      // renders the "not in the target group" / "not open" states the server answers with. The SUBMIT it
      // makes is identity-free (ADR-0009 §2) even though the page around it is a normal logged-in view.
      {
        path: "questionnaires/:id/respond",
        element: <QuestionnaireFormPage />,
        handle: titled("questionnaireForm.title"),
      },
      // KURSSIT (US-2.1/2.2) — the course catalogue, and since the owner's decision of 3.11.2026 that
      // alone. Between 8.8.2026 and that decision this path was "Sisällönhallinta" and carried the
      // questionnaires (KM-E1) beside the courses, which is why its gate admitted EITHER capability:
      // contentManagement is {ADMIN, CONTENT_MANAGER} and questionnaireManagement is {ADMIN, HR},
      // overlapping only in ADMIN, so a single-capability guard would have locked HR out of the
      // questionnaire half.
      //
      // The gate is `contentManagement` alone now, and that narrows nobody's authority: the
      // questionnaire half is its own route below with `questionnaireManagement` on it, which is the
      // capability it always needed. An HR caller who hand-types this path is bounced home by the guard
      // rather than shown a course table they may not use.
      {
        path: "admin/courses",
        element: (
          <RequireRole capability="contentManagement">
            <ContentPage />
          </RequireRole>
        ),
        handle: titled("courses.listTitle"),
      },
      // The handle here is the GENERIC title ("Koulutus"): the real one is the course's name, which
      // the page only knows after its fetch and feeds in with usePageTitle(). That is what the bar
      // reads while the route loads — see ./pageTitle.
      {
        path: "admin/courses/:id",
        element: (
          <RequireRole capability="contentManagement">
            <CourseEditPage />
          </RequireRole>
        ),
        handle: titled("courses.editTitle"),
      },
      // The AUTHOR'S PREVIEW of one content object (owner's decision 5.11.2026; ADR-0029) — the SCO run in
      // the learner's own player, under the learner's own SEC-1 isolation, with nothing recorded as a
      // completion.
      //
      // A CHILD PATH of the course editor, not a sibling of the learner's `/learn/:enrollmentId/...`, and
      // that is the decision worth reading: the preview is addressed by COURSE + CONTENT OBJECT, because it
      // has no enrollment and no attempt to be addressed by, and nesting it under the editor is what gives
      // the page its way back ("Takaisin editoriin") without a `?return=` to resolve. The learner route's
      // `?return=` exists because a learner arrives from several places; an author arrives from exactly one.
      //
      // `contentManagement` here as well as on the API: a hand-typed URL is bounced home by the guard
      // rather than drawing a player shell whose first call would 403.
      {
        path: "admin/courses/:id/preview/:contentObjectId",
        element: (
          <RequireRole capability="contentManagement">
            <ScormPreviewPage />
          </RequireRole>
        ),
        handle: titled("scormPreview.title"),
      },
      // Training assignment (US-5.1) + group management (US-5.3). Gated to the
      // training-assignment role (ADMIN/HR) — matching the server-side 403 — so a
      // hand-typed URL bounces home instead of rendering an admin view.
      {
        path: "admin/assign",
        element: (
          <RequireRole capability="trainingAssignment">
            <AssignTrainingPage />
          </RequireRole>
        ),
        handle: titled("assign.title"),
      },
      {
        path: "admin/groups",
        element: (
          <RequireRole capability="trainingAssignment">
            <GroupsPage />
          </RequireRole>
        ),
        handle: titled("groups.title"),
      },
      // Tenant-admin user administration + 2FA reset (US-1.5) and organisation structure (US-1.7).
      // Gated to the user-administration role (tenant ADMIN) — matching the server-side 403 — so a
      // hand-typed URL bounces home instead of rendering an admin view.
      {
        path: "admin/users",
        element: (
          <RequireRole capability="userAdministration">
            <UsersPage />
          </RequireRole>
        ),
        handle: titled("users.title"),
      },
      {
        path: "admin/organization",
        element: (
          <RequireRole capability="userAdministration">
            <OrganizationPage />
          </RequireRole>
        ),
        handle: titled("org.title"),
      },
      // Kyselymoottori view-scope GRANTS (KM-E7 / US-E7.1 vaihe 1, FR-Q.48): the tenant-pääkäyttäjä grants
      // and revokes a johdon raporttikäyttäjä's näkymälaajuudet — the company and/or named units — and
      // that grant is the ONLY thing that decides how wide the leadership report reads. Until this route
      // existed the write had no caller outside the E2E fixtures, so the role shipped with no scopes and
      // `/reports/leadership` could only tell its reader to go and ask somebody.
      //
      // The gate is `viewScopeGrantManagement`, NOT `userAdministration`: the API's own set is
      // { ADMIN, HR } and the HR expert is the person the requirement names, so guarding it like the user
      // directory (ADMIN alone) would lock half of its authorized audience out of their own authority. It
      // sits under /admin because granting is an administrative act over people, not a report.
      {
        path: "admin/view-scopes",
        element: (
          <RequireRole capability="viewScopeGrantManagement">
            <ViewScopeGrantsPage />
          </RequireRole>
        ),
        handle: titled("viewScopes.title"),
      },
      // Kyselymoottori questionnaire management (KM-E1 / US-E1.1): the engine's questionnaire list and
      // the "Uusi kysely" MODE-ROUTER creation page (koe → course creation + SCORM import, palaute
      // disabled, anonyymi → the engine's draft form). The legacy /admin/surveys module it replaced was
      // retired at parity (ADR-0006). Gated to the questionnaire-management role (ADMIN/HR)
      // — matching QuestionnaireEndpoints' server-side RequireAnyRole — so a learner is bounced home,
      // not just 403'd. The static `new` route ranks above `:id`.
      //
      // The LIST is a page again as of 3.11.2026 (task c4a71fc1), and this is the path it had before
      // 8.8.2026: between those dates it was a group and then a tab of the content page, and this entry
      // was a <Navigate> standing in for it. The redirect is gone because its target is gone — the
      // bookmarks it existed for now reach the thing they always meant, with no hop.
      //
      // The rail row points here and is PREFIX-matched (AppLayout), so it stays lit on every surface
      // below: the editor, the participation report, the result report, the quote workspace and the
      // settings all hang under admin/questionnaires/:id.
      {
        path: "admin/questionnaires",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireListPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.title"),
      },
      {
        path: "admin/questionnaires/new",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireEditPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.editTitle"),
      },
      {
        path: "admin/questionnaires/:id",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireEditPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.editTitle"),
      },
      // Kyselymoottori RESULT REPORT (KM-E6 / US-E6.2): the HR/ADMIN view of a CLOSED questionnaire's
      // tulosraportti — questions, factors + nelikenttä, themes, eNPS and the background comparisons, all
      // rendered exactly as the server redacted them, with the thresholds that shaped them (FR-Q.22). The
      // optional background filter travels as `?backgroundKey=…&backgroundValue=…` so a reload keeps the
      // scope. Same questionnaire-management gate (ADMIN/HR) as the rest of the engine surface. Ranks
      // below `:id` in specificity terms but is a distinct path, so order does not matter here.
      {
        path: "admin/questionnaires/:id/report",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireReportPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.report.title"),
      },
      // Kyselymoottori PARTICIPATION report (KM-E6 / US-E6.1): the coverage view of a questionnaire that is
      // COLLECTING — the frozen audience, the count of "kyselyn avanneet" (openers, marked at token grant,
      // US-E4.1) and the share they make up, with no response content whatsoever. It is the only view there
      // is while a questionnaire is open, and it keeps answering after the close (the final coverage). A
      // DRAFT answers 409: its audience is frozen at publish, so it has no coverage to state. Same
      // questionnaire-management gate (ADMIN/HR) as the rest of the engine surface.
      {
        path: "admin/questionnaires/:id/participation",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireParticipationPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.participation.title"),
      },
      // Kyselymoottori QUOTE WORKSPACE (KM-E8 / US-E8.2, spec luku 11.3): the tenant-pääkäyttäjä's reading
      // surface over a closed questionnaire's OPEN answers — listed per question, with no background
      // variables and no link to a numeric response row, plus the markings (teema/nosto/ohita) and the
      // clipboard copy the expert composes a customer version from. It sits under /admin, not /reports,
      // because it is deliberately NOT a report: FR-Q.41 makes it the expert's workspace and FR-Q.50 keeps
      // leadership out of it, so it carries the questionnaire-MANAGEMENT gate (ADMIN/HR) rather than the
      // report-READ one — a JOHDON_RAPORTTI holder is bounced home here and 403'd by the API.
      {
        path: "admin/questionnaires/:id/quotes",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireQuotesPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.quotes.title"),
      },
      // Kyselymoottori TENANT SETTINGS (KM-E9 / US-E9.2b, spec luku 7.3): one questionnaire's parameters
      // grouped by the CLASS the server put them in — the tenant-free ones with their controls, the ones
      // the publish locked with the server's reason beside them, and the derived/locked rows read-only so
      // an expert can see why a refusal happened. Same questionnaire-management gate (ADMIN/HR) as the
      // rest of the engine surface; the PLATFORM class is deliberately not on this page (see below).
      {
        path: "admin/questionnaires/:id/settings",
        element: (
          <RequireRole capability="questionnaireManagement">
            <TenantSettingsPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.settings.tenantTitle"),
      },
      // Certificate OPERATIONS (E6 / US-6.1): the tenant's failed and stuck certificate renders with a
      // per-row retry, and the lookup that opens a learner's certificate by the identifier printed on it.
      // It is the counterweight to the learner contract having no failure state — a failed render is shown
      // to its holder as "muodostetaan" because they can do nothing about it, which is honest only while
      // somebody else sees the failure. Until this route existed nobody did: the endpoints had no caller in
      // the SPA at all.
      //
      // Gated on `certificateOperations` ({ ADMIN }) — matching CertificateEndpoints' own
      // RequireAnyRole(Roles.Admin) on every handler in the group — so a learner is bounced home rather
      // than 403'd. It sits under /admin and not under the learner's /certificates tree on purpose: the
      // subject of this page is other people's documents.
      {
        path: "admin/certificates",
        element: (
          <RequireRole capability="certificateOperations">
            <CertificateOpsPage />
          </RequireRole>
        ),
        handle: titled("certificateOps.title"),
      },
      // Compliance coverage report (E7, US-7.1/7.2): the koonti + per-person rows with filters and
      // CSV/PDF export. Gated to the compliance-reporting roles (ADMIN/HR/ESIMIES) — matching the
      // server-side 403 — so a learner is bounced home. Breadth (tenant-wide vs. own team) is decided
      // server-side by the row-level scope, so the same page serves HR/admin and a manager.
      {
        path: "reports/coverage",
        element: (
          <RequireRole capability="complianceReporting">
            <CoverageReportPage />
          </RequireRole>
        ),
        handle: titled("reports.coverage.title"),
      },
      // Kyselymoottori LEADERSHIP report (KM-E7 / US-E7.1, spec luku 10.4): the same result document as the
      // HR report, over ONE view scope the caller was granted (US-E9.1) — the rollups, the nelikenttä, the
      // eNPS, the thresholds and this scope's response rate, with no quotes, no participant names and no
      // per-unit breakdown (FR-Q.47/50). It sits under /reports rather than /admin because the audience is
      // not an administrator: the JOHDON_RAPORTTI role opens this page and nothing else in the engine.
      // Gated on the report-READ capability { ADMIN, HR, JOHDON_RAPORTTI } — matching the endpoint's own
      // RequireAnyRole — so a learner is bounced home instead of 403'd. Which scopes the page may show is
      // NOT part of this gate: the server answers that, per caller, from the grants.
      // …and the way IN to it (US-E7.1c): the measurements this caller may open, from the same read the
      // baseline picker uses (`GET /api/questionnaires/measurements`). It is the LANDING the nav entry
      // points at, because a JOHDON_RAPORTTI holder holds no other list — the management list is a 403 for
      // them — and without it the report above was reachable only from a link somebody handed over. The
      // static path ranks above `:id`, and carries the same report-READ gate.
      {
        path: "reports/leadership",
        element: (
          <RequireRole capability="questionnaireReportRead">
            <LeadershipReportListPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.leadership.list.title"),
      },
      {
        path: "reports/leadership/:id",
        element: (
          <RequireRole capability="questionnaireReportRead">
            <LeadershipReportPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.leadership.title"),
      },
      // Kyselymoottori RESULT-REPORT LANDING for HR/ADMIN (KM-E6 / US-E6.2 — the way in). The list of CLOSED
      // questionnaires, each row a door to its `admin/questionnaires/:id/report` above. It reads
      // `GET /api/questionnaires?status=closed`, whose own gate is `Roles.QuestionnaireManagement`, and it
      // renders that answer as given — the server orders by `UpdatedAt`, not by `ClosesAt`, and the page
      // does not re-sort it (the ordering is the server's claim to make; changing it is a change to
      // `QuestionnaireAdminService.ListAsync`).
      //
      // It lives under /reports, beside the leadership landing, and it stays there: it is a report, and
      // the rail row for it now sits under the KYSELYT section (AppLayout, 3.11.2026) beside the list and
      // the leadership landing, which is where its reader looks for it. Moving the path under
      // `admin/questionnaires/` would also collide with `:id` — every segment there names one
      // questionnaire. The gate is questionnaire-MANAGEMENT, not the report-READ capability of the two
      // routes above: report-read also holds JOHDON_RAPORTTI, and this page's every read would 403 for
      // them — they have `/reports/leadership`, which is theirs.
      {
        path: "reports/questionnaires",
        element: (
          <RequireRole capability="questionnaireManagement">
            <QuestionnaireResultsListPage />
          </RequireRole>
        ),
        handle: titled("questionnaires.report.list.title"),
      },
      // ── Muutosjohtaminen (ADR-0016) · the consultant's module ──────────────────────────────────────
      //
      // Seven routes: the programme list, and the programme itself on the six paths its tab strip walks
      // (Yleiskuva · Mittarit · Työpajat · Päätökset ja toimenpiteet · Seuranta · Roolit — see
      // ChangeProgramPage for why the tabs are routes and why two of the stories share one).
      //
      // All six programme routes carry the SAME title handle, and on purpose: the shell's <h1> names
      // the PROGRAMME and the tab strip names the surface, so a heading that changed with the tab would
      // say twice what the selected tab already says and leave the container unnamed. "Muutosohjelma" is
      // therefore the standing generic title, which MJ-US-E1.2 replaces with the programme's real name
      // through usePageTitle() once the page has a read to get it from — exactly as CourseEditPage does.
      //
      // TOP LEVEL, under neither /admin nor /reports, and that is a decision about who the reader is.
      // ADR-0016 §4.5 property 1 keeps the tenant ADMIN out of `Roles.ChangeProgramManagement`
      // altogether, so /admin — the tree whose every other route this reader cannot open — would file the
      // module under an authority its only holder does not have. And it is not reporting: the consultant
      // WRITES here, and the one read surface leadership gets (§4.5's published report) is not in this
      // frame at all.
      //
      // `denied="forbidden"` rather than the tree-wide bounce home, and this is the only place that
      // argues for it: the consultant is an Incedo person named into customer tenants one at a time
      // (ADR-0016 §4.2 on FR-Q.33), so the way to arrive here without the role is to open a programme URL
      // against the WRONG tenant. "Nothing here" is false for that reader and unactionable; the refusal
      // is both true and what MJ-US-E1.1's "Rajattu / estetty … → 403" asks to be shown. See
      // ./RequireRole for the full comparison with the default.
      //
      // The static list path is declared first and the six programme paths are distinct, so the
      // `:id` route can never swallow the list.
      {
        path: "change-programs",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramListPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.list.title"),
      },
      {
        path: "change-programs/:id",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      {
        path: "change-programs/:id/metrics",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      {
        path: "change-programs/:id/workshops",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      {
        path: "change-programs/:id/decisions",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      {
        path: "change-programs/:id/tracking",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      // MJ-US-E6.1's Raportti tab — the kvartaaliraportin luonnos. The SAME gate as every other tab of
      // this module: ADR-0016 §4.5 gives the draft to `MJ_VASTUUKONSULTTI` alone, and property 2 is why
      // there is no leadership route beside it — a client role reaches a PUBLISHED report (MJ-US-E6.2) and
      // never a draft, so a page in that tree would 403 every read for its only reader.
      {
        path: "change-programs/:id/report",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      // MJ-US-E7.1's Roolit tab. The SAME gate as the other six, which is the story's own property 4
      // (ADR-0016 §4.5): granting is done TO leadership, never BY it — a surface that let the leadership
      // reader in would make every rule downstream of the grant advisory. There is deliberately no
      // /admin route beside it: the tenant ADMIN is out of `Roles.ChangeProgramManagement` on purpose
      // (property 1), so a page in that tree would 403 every read for its only reader.
      {
        path: "change-programs/:id/roles",
        element: (
          <RequireRole capability="changeProgramManagement" denied="forbidden">
            <ChangeProgramPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.programTitle"),
      },
      // MUUTOSKARTTA (MJ-US-E6.3, ADR-0016 §7.7) — the module's one TENANT-wide view: every change
      // programme of this customer on one timeline.
      //
      // ITS OWN TOP-LEVEL PATH, outside the `change-programs` tree, and that is not tidiness. Those seven
      // routes are one programme's case file and every one of them is the consultant's alone
      // (change-management-frame.test.mjs holds them to exactly one capability and one `denied`); this one
      // has no `:id` because its question is about the whole tenant, and it has a SECOND AUDIENCE the
      // others cannot have. Hanging it under `change-programs/` would put a route a leadership reader may
      // open inside a subtree whose every sibling 403s for them.
      //
      // THE GATE ADMITS EITHER CAPABILITY, and the page decides which read to make from the same pair:
      // `changeProgramManagement` opens the LIVE map, `changePublishedReportRead` alone opens the
      // published one. The two capabilities are not nested — the consultant holds both and the leadership
      // reader holds only the second — so a single-capability guard would lock one of the two audiences
      // out of the page the story is about. Which of the two ROUTES answers is never a branch inside a
      // service (§7.7, §8.1): the SPA simply never calls the live route without the consultant's
      // capability, and the route table 403s it if it ever did.
      //
      // `denied="forbidden"` for the module's own reason (see the block above and ./RequireRole): both of
      // this page's readers are named into customer tenants one at a time, so arriving without the role is
      // most likely the WRONG TENANT — and "nothing here" is the one answer that is false for them.
      {
        path: "change-map",
        element: (
          <RequireRole
            capability={["changeProgramManagement", "changePublishedReportRead"]}
            denied="forbidden"
          >
            <ChangeMapPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.changeMap.title"),
      },
      // JULKAISTU KVARTAALIRAPORTTI (MJ-US-E6.2, ADR-0016 §4.5's one client row, §8.1) — the published
      // snapshot of ONE programme's quarter, and the only surface in this module that puts a figure in
      // front of the customer's leadership.
      //
      // ITS OWN TOP-LEVEL PATH for `change-map`'s reason exactly: the `change-programs/:id/*` tree is one
      // programme's case file and every route in it is the consultant's alone, so a route a leadership
      // reader may open cannot live inside a subtree whose every sibling 403s for them. It carries an
      // `:id` because the report is about one programme — the question "which programmes have published
      // one" is the muutoskartta's, which is where this page is linked from.
      //
      // THE GATE ADMITS EITHER CAPABILITY, which is §4.5's row: the consultant is on it because
      // previewing what leadership sees is part of publishing it, and the johdon raporttikäyttäjä because
      // this is their report. No branch in the page follows from which of the two is held — there is one
      // route and one answer, and that is §8.1 rather than a simplification.
      //
      // `denied="forbidden"` for the module's reason: both readers are named into customer tenants one at
      // a time, so arriving without the role is most likely the WRONG TENANT, and "nothing here" is the
      // one answer that is false for them.
      {
        path: "change-report/:id",
        element: (
          <RequireRole
            capability={["changeProgramManagement", "changePublishedReportRead"]}
            denied="forbidden"
          >
            <ChangeReportPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.published.title"),
      },
      // OMAT TOIMENPITEET (MJ-US-E7.2, ADR-0016 §4.5 property 3) — the action owner's own work list, and
      // the only surface in this module whose reader is an ordinary employee of the customer.
      //
      // ITS OWN TOP-LEVEL PATH, outside the `change-programs` tree, for `change-map`'s reason in a sharper
      // form: those routes are one programme's case file and the consultant's alone, and this one has no
      // `:id` at all because its subject is the CALLER — the list spans every change programme of the
      // tenant and names none of them (`/api/my/change-actions`, §7.2). Nor is it under `/admin` or
      // `/reports`: the person reading it administers nothing and reads no report, they are reporting on
      // their own work. The shape it follows is the learner's own surfaces (`/certificates`,
      // `/questionnaires`), which are likewise top-level and likewise about the signed-in person.
      //
      // The DEFAULT `denied`, and this is the one route of the module that argues for the default rather
      // than against it. `denied="forbidden"` exists for the Incedo consultant who opened a programme URL
      // against the wrong customer tenant (./RequireRole), a mistake an action owner cannot make: they are
      // a person of exactly one tenant, with a learner home in it that is theirs. A reader who arrives here
      // without the code is somebody the role was never granted to or was withdrawn from, and moving them
      // to the page they do have is both true and actionable. The story's own refusals are the API's four
      // (403/404/422/409), which the page renders; a route-level denial is not among them.
      {
        path: "change-actions",
        element: (
          <RequireRole capability="changeOwnActions">
            <MyChangeActionsPage />
          </RequireRole>
        ),
        handle: titled("changePrograms.ownActions.title"),
      },
      // TYÖSUHDETIEDOT (ADR-0020 §4, §16 task 5) — the employment-record list and one person's record.
      //
      // ITS OWN TOP-LEVEL TREE, `/hrm/*`, and not under `/admin`: an employment record is not an
      // administrative object. The tenant ADMIN opens it — `employmentRecordView` mirrors
      // `Roles.EmploymentRecordView`, which is that administrator AND the HRM expert — but so does an
      // Incedo HRM expert who administers nothing, and §5's whole argument is that the two authorities
      // answer different questions. `/admin/employment-records` would file the record under the one role
      // that holds the LEAST of it.
      //
      // The static list path is declared first and `:userId` is distinct from it, so the record route can
      // never swallow the list.
      //
      // `denied="forbidden"` rather than the learner home, which is RequireRole's own rule for a surface
      // whose refusal most likely means the WRONG TENANT: the HRM expert may be Incedo's external one,
      // named into several customer tenants one at a time (ADR-0020 §5), and "nothing here" is the one
      // answer that is false for them. The muutosjohtaminen routes above make the same call for the same
      // reason.
      //
      // WHAT THE GATE DOES NOT DECIDE: the two reveals and the work permits. Those sit behind
      // `hrmPersonnelRecord` — the HRM expert ALONE — and are checked INSIDE the record page, because an
      // administrator may open the record and may not open those three things. A second route would have
      // been the wrong shape: it is one page, with one subject, and two authorities over parts of it.
      {
        path: "hrm/employment-records",
        element: (
          <RequireRole capability="employmentRecordView" denied="forbidden">
            <EmploymentRecordsPage />
          </RequireRole>
        ),
        handle: titled("employmentRecords.title"),
      },
      {
        path: "hrm/employment-records/:userId",
        element: (
          <RequireRole capability="employmentRecordView" denied="forbidden">
            <EmploymentRecordPage />
          </RequireRole>
        ),
        handle: titled("employmentRecord.title"),
      },
      // Audit-log viewer (US-8.1): read-only tenant audit trail + integrity indicator. Gated to the
      // audit-read capability (auditor/HR/tenant admin) — matching AuditEndpoints' server-side roles.
      {
        path: "admin/audit",
        element: (
          <RequireRole capability="auditRead">
            <AuditLogPage />
          </RequireRole>
        ),
        handle: titled("audit.title"),
      },
      // ADR-0018 §8 — Verson tukipääsy: the tenant's own, read-only view of every support window Verso
      // has opened into it, with its reason, its window and whether it was ever used. Gated to
      // `supportAccessVisibility` ({ ADMIN, HR }) — matching `TenantSupportAccessEndpoints`' own
      // `RequireAnyRole(Roles.TenantWide)`, so a COMPLIANCE_AUDITOR who holds audit-read is bounced
      // home here rather than 403'd by the API: they read the CHAIN, and acting on a live window is the
      // pääkäyttäjä's.
      //
      // There is no write route beside it and none is added. Opening and closing a support window is
      // Verso's act on the operator console's own client (ADR-0014 §6 phase 2), which is the same split
      // §7's role grants take: the platform writes, the tenant reads.
      {
        path: "admin/support-access",
        element: (
          <RequireRole capability="supportAccessVisibility">
            <SupportAccessPage />
          </RequireRole>
        ),
        handle: titled("supportAccess.title"),
      },
      // There are no `platform/*` routes here any more. ADR-0014 §6 phase 2 moved the operator's
      // console — the cross-tenant tenant list (US-1.6) and the platform-level questionnaire
      // parameters (KM-E9) — to its own SPA entry on its own host, authenticating with its own
      // Keycloak client (`apps/platform`, task C-1). The client is the point: C-2's guard checks the
      // client AND the role, and a surface served from the product's origin on the product's client
      // cannot satisfy it. A PLATFORM_ADMIN who hand-types the old path lands on the NotFound below,
      // which is the truth — this app does not answer for the platform any more. The customer's own
      // pääkäyttäjä (ADMIN) surfaces above are unchanged by the move.
      { path: "*", element: <NotFoundPage />, handle: titled("notFound.heading") },
    ],
  },
];

// `routes` is the table; the ROUTER hangs it all under one pathless route whose element is the page
// title channel (./pageTitle `PageTitleRoot`), so the title resolved from the matched handle reaches
// the browser tab on every route — the pre-session ones included — and the shell's <h1> below it.
// The wrapper is added HERE and not in the array on purpose: `routes` stays the plain table that
// `resolveExitTarget` matches candidate URLs against, with no element of its own to explain.
export const router = createBrowserRouter([{ element: <PageTitleRoot />, children: routes }]);
